import React, { Component } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Theme, Typography } from '@mui/material';

import { AnnotationTemplate, AnnotationTemplateType } from '@paragon/contract-library';
import {
  getLoggedInUser,
  hasOrganizationTemplatesPermission,
  hasSealsAndSignaturesPermission,
} from '@paragon/login-tools';
import { enqueueError, enqueueWarning, NotifierConfirm } from '@paragon/notification-tools';

import { toggleSystemAnnotationsEditableAndDeselectAll } from '../../actions/annotations';
import { editTemplateAnnotations, saveModifiedTemplates } from '../../actions/annotationTemplates';
import {
  applySelectedTemplateToActivePage,
  applySelectedTemplateToActivePageWithoutTranslation,
  applySelectedTemplateToAllPagesInPdf,
  applySelectedTemplateToFirstPageOfEveryComponentPdf,
  downloadSelectedAnnotationsOnCurrentPage,
} from '../../actions/annotationTools';
import { showSaveNewAnnotationTemplatePopup } from '../../actions/popup';
import { getSelectedAnnotationsForCurrentPage } from '../../selectors/annotations';
import { getSelectedTemplate } from '../../selectors/annotationTemplates';
import { deleteAnnotationTemplate, editAnnotationTemplate } from '../../store/annotationTemplates';
import VisibleUserSelect from './VisibleUserAnnotations/VisibleUserSelect';

const buttonSx = {
  my: 1,
  py: 1,
  width: '100%',
  bgcolor: 'backContrast.light',
  '&:hover': {
    bgcolor: 'primary.dark',
    color: '#fff',
  },
} as const;

const optionsButtonSx = {
  bgcolor: 'backContrast.light',
  '&:hover': {
    bgcolor: 'primary.dark',
    color: '#fff',
  },
} as const;

const headingSx = (theme: Theme) =>
  ({
    fontSize: theme.typography.pxToRem(15),
    fontWeight: theme.typography.fontWeightRegular,
  }) as const;

const templateSectionSx = {
  width: '100%',
  textAlign: 'left',
} as const;

const applyDivSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  mt: 1,
  mb: 2,
  border: 'solid 2px',
  borderColor: 'backContrast.light',
  p: 1,
} as const;

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;

interface OwnState {
  readonly updateTemplateOpen: boolean;
  readonly deleteTemplateOpen: boolean;
}

class AnnotationsAccordion extends Component<StateProps & DispatchProps, OwnState> {
  state: OwnState = {
    updateTemplateOpen: false,
    deleteTemplateOpen: false,
  };

  onSaveNewTemplate = () => {
    const { selectedAnnotationsOnCurrentPage, showSaveNewAnnotationTemplatePopup } = this.props;

    if (selectedAnnotationsOnCurrentPage.length === 0) {
      this.props.enqueueWarning('Please select at least one annotation');
      return;
    }

    showSaveNewAnnotationTemplatePopup(selectedAnnotationsOnCurrentPage);
  };

  onUpdateTemplate = () => {
    if (this.props.selectedTemplateId == null) {
      this.props.enqueueError('Error: Cannot update annotation template because none is selected.');
      return;
    }

    if (this.props.selectedAnnotationsOnCurrentPage.length === 0) {
      this.props.enqueueWarning('Please select at least one annotation');
      return;
    }

    this.setState({ updateTemplateOpen: true });
  };

  onConfirmUpdateTemplate = () => {
    const { selectedTemplateId, editTemplateAnnotations, saveModifiedTemplates } = this.props;
    editTemplateAnnotations(selectedTemplateId, this.props.selectedAnnotationsOnCurrentPage);
    saveModifiedTemplates();
    this.closeUpdateTemplate();
  };

  closeUpdateTemplate = () => {
    this.setState({ updateTemplateOpen: false });
  };

  onDeleteTemplate = () => {
    const { selectedTemplateId } = this.props;

    if (selectedTemplateId == null) {
      this.props.enqueueError('Error: Cannot delete annotation template because none is selected.');
      return;
    }

    this.setState({ deleteTemplateOpen: true });
  };

  onConfirmDeleteTemplate = () => {
    const { deleteAnnotationTemplate, selectedTemplateId, saveModifiedTemplates } = this.props;

    deleteAnnotationTemplate(selectedTemplateId);
    saveModifiedTemplates();
    this.setState({ deleteTemplateOpen: false });
  };

  closeDeleteTemplate = () => {
    this.setState({ deleteTemplateOpen: false });
  };

  editIsOrganizationTemplate = (_: any, checked: boolean) => {
    const { editAnnotationTemplate, selectedTemplate, user } = this.props;
    if (!selectedTemplate) throw new Error('Annotation template not selected');
    if (!user) throw new Error('User not found.');
    editAnnotationTemplate({
      templateId: selectedTemplate.id,
      editedProperties: {
        organization: checked ? user.organization : null,
        userEmail: checked ? null : user.email,
      },
    });
  };

  editAnnotationTemplateType = (annotationTemplateType: AnnotationTemplateType) => {
    const { editAnnotationTemplate, selectedTemplate } = this.props;
    if (!selectedTemplate) throw new Error('Annotation template not selected');
    editAnnotationTemplate({
      templateId: selectedTemplate.id,
      editedProperties: { templateType: annotationTemplateType },
    });
  };

  render() {
    const { selectedTemplate, hasOrganizationTemplatesPermission } = this.props;

    const isOrganizationTemplate =
      !!selectedTemplate && selectedTemplate.userEmail === null && selectedTemplate.organization !== null;

    return (
      <Box sx={{ width: '100%', py: 2 }}>
        <Accordion sx={{ bgcolor: 'backContrast.main' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={headingSx}>Template Tools</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={templateSectionSx}>
              <Typography variant="caption">Options</Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 1,
                  mb: 2,
                  border: 'solid 2px',
                  borderColor: 'backContrast.light',
                  p: 1,
                }}
              >
                <Button sx={optionsButtonSx} variant="outlined" onClick={this.onSaveNewTemplate}>
                  Save New
                </Button>
                <Button
                  sx={optionsButtonSx}
                  variant="outlined"
                  onClick={this.onUpdateTemplate}
                  disabled={isOrganizationTemplate && !hasOrganizationTemplatesPermission}
                >
                  Update
                </Button>
                <Button
                  sx={optionsButtonSx}
                  variant="outlined"
                  onClick={this.onDeleteTemplate}
                  disabled={isOrganizationTemplate && !hasOrganizationTemplatesPermission}
                >
                  Delete
                </Button>
              </Box>
            </Box>
            <Box sx={templateSectionSx}>
              <Typography variant="caption">Apply Selected Template</Typography>
              <Box sx={applyDivSx}>
                <Button variant="outlined" sx={buttonSx} onClick={this.props.applySelectedTemplateToActivePage}>
                  To This Page
                </Button>
                <Button
                  variant="outlined"
                  sx={buttonSx}
                  onClick={this.props.applySelectedTemplateToActivePageWithoutTranslation}
                >
                  Without Translation
                </Button>
                <Button variant="outlined" sx={buttonSx} onClick={this.props.applySelectedTemplateToAllPagesInPdf}>
                  To This PDF&apos;s Pages
                </Button>
                <Button
                  variant="outlined"
                  sx={buttonSx}
                  onClick={this.props.applySelectedTemplateToFirstPageOfEveryComponentPdf}
                >
                  To First Page of Every Component
                </Button>
              </Box>
            </Box>
            <Box sx={templateSectionSx}>
              <Typography variant="caption">Other</Typography>
              <Box sx={applyDivSx}>
                <Button variant="outlined" sx={buttonSx} onClick={this.props.downloadSelectedAnnotationsOnCurrentPage}>
                  Download Selected
                </Button>
                {this.props.hasSealsAndSignaturesPermission && (
                  <Button
                    variant="outlined"
                    sx={buttonSx}
                    onClick={this.props.toggleSystemAnnotationsEditableAndDeselectAll}
                  >
                    {this.props.systemAnnotationsEditable ? 'Disable' : 'Enable'} System Annotation Editing
                  </Button>
                )}
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
        <Accordion sx={{ bgcolor: 'backContrast.main' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography sx={headingSx}>Visible User Annotations</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <VisibleUserSelect />
          </AccordionDetails>
        </Accordion>
        <NotifierConfirm
          open={this.state.updateTemplateOpen}
          onClose={this.closeUpdateTemplate}
          onClick={this.onConfirmUpdateTemplate}
          message={`Are you sure you would like to update template '${
            selectedTemplate ? selectedTemplate.name : 'unknown'
          }'?`}
          buttonText="Update"
        />
        <NotifierConfirm
          open={this.state.deleteTemplateOpen}
          onClose={this.closeDeleteTemplate}
          onClick={this.onConfirmDeleteTemplate}
          message={`Are you sure you would like to delete template '${
            selectedTemplate ? selectedTemplate.name : 'unknown'
          }'?`}
          buttonText="Delete"
        />
      </Box>
    );
  }
}

function mapStateToProps(state: any) {
  const selectedTemplateId = state.annotationTemplates.selectedTemplate;
  const selectedTemplate = getSelectedTemplate(state) as AnnotationTemplate | null;

  return {
    systemAnnotationsEditable: state.annotations.systemAnnotationsEditable,
    selectedAnnotationsOnCurrentPage: getSelectedAnnotationsForCurrentPage(state),

    selectedTemplateId,
    selectedTemplate,

    user: getLoggedInUser(state),
    hasSealsAndSignaturesPermission: hasSealsAndSignaturesPermission(state),
    hasOrganizationTemplatesPermission: hasOrganizationTemplatesPermission(state),
  };
}

const actionCreators = {
  toggleSystemAnnotationsEditableAndDeselectAll,
  downloadSelectedAnnotationsOnCurrentPage,
  applySelectedTemplateToActivePageWithoutTranslation,
  applySelectedTemplateToActivePage,
  applySelectedTemplateToAllPagesInPdf,
  applySelectedTemplateToFirstPageOfEveryComponentPdf,
  showSaveNewAnnotationTemplatePopup,
  editAnnotationTemplate,
  editTemplateAnnotations,
  deleteAnnotationTemplate,
  saveModifiedTemplates,
  enqueueWarning,
  enqueueError,
};

export default connect(mapStateToProps, actionCreators)(AnnotationsAccordion);
